import { computed } from 'vue';
import { useDashboardTab } from '@/composables/admin/dashboard/useDashboardTab';
import { getActivity } from '@/services/dashboard/dashboard';
import { getSeasonName } from '@/composables/admin/dashboard/seasonUtils';

/**
 * Reglas de clasificación para eventos de ingreso extra.
 * Se evalúan en orden: primer match gana.
 * Cada regla tiene un key (para locales) y un patrón regex (case-insensitive).
 * @type {Array<{key: string, pattern: RegExp}>}
 */
const CATEGORY_RULES = [
    { key: 'experiencia', pattern: /experienc/i },
    { key: 'foto',        pattern: /foto/i },
    { key: 'tour',        pattern: /tour/i },
    { key: 'merchandising', pattern: /camiseta/i },
    { key: 'filiales',    pattern: /imperial|division.?de.?honor|promises|femenino/i },
];

/** @param {string} eventName */
function classifyEvent(eventName) {
    for (const rule of CATEGORY_RULES) {
        if (rule.pattern.test(eventName)) return rule.key;
    }
    return 'otros';
}

/**
 * Composable del panel de ingresos extra del dashboard.
 * Muestra eventos sin jornada (experiencias, filiales, etc.)
 * con desglose por categoría.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef
 * @param {import('vue').Ref<Array>} seasonsRef
 * @returns {{
 *     data: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     summaryKpis: import('vue').ComputedRef<object|null>,
 *     extraIncomeBreakdown: import('vue').ComputedRef<Array>,
 *     eventsTable: import('vue').ComputedRef<Array>,
 *     activityComparison: import('vue').ComputedRef<Array|null>,
 *     activityDelta: import('vue').ComputedRef<object|null>,
 *     seasonBreakdown: import('vue').ComputedRef<null>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useActivity(seasonIdsRef, seasonsRef) {
    const base = useDashboardTab(seasonIdsRef, seasonsRef, getActivity);

    /**
     * KPIs resumen — en single season usa la primera temporada.
     * En multi-season se devuelve null y se usa activityComparison.
     */
    const summaryKpis = computed(() => {
        if (base.isMultiSeason.value) return null;
        const perSeason = base.data.value?.data?.per_season || {};
        const firstId = seasonIdsRef.value[0];
        return perSeason[firstId]?.summary || null;
    });

    /**
     * Lista de eventos de todas las temporadas, ordenada por fecha descendente.
     */
    const eventsTable = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const allEvents = [];
        for (const [, entry] of Object.entries(perSeason)) {
            const activity = entry?.activity;
            if (activity?.length) {
                allEvents.push(...activity);
            }
        }
        if (!allEvents.length) return [];
        return [...allEvents].sort(
            (a, b) => new Date(b.event_date) - new Date(a.event_date),
        );
    });

    /**
     * Datos comparativos de resumen por temporada (multi-season).
     */
    const activityComparison = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.summary,
        );
        if (entries.length < 2) return null;

        return entries.map(([seasonId, entry]) => {
            const s = entry.summary || {};
            return {
                season: getSeasonName(seasonsRef.value, seasonId),
                totalEvents: s.total_events || 0,
                totalRevenue: s.total_revenue || 0,
                totalAttendance: s.total_attendance || 0,
            };
        });
    });

    /**
     * Deltas del resumen cuando son exactamente 2 temporadas.
     */
    const activityDelta = computed(() => {
        const comp = activityComparison.value;
        if (!comp || comp.length !== 2) return null;
        return {
            totalEvents: comp[1].totalEvents - comp[0].totalEvents,
            totalRevenue: comp[1].totalRevenue - comp[0].totalRevenue,
            totalAttendance: comp[1].totalAttendance - comp[0].totalAttendance,
        };
    });

    /**
     * Desglose por temporada — no aplica (se usa activityComparison).
     */
    const seasonBreakdown = computed(() => null);

    /**
     * Desglose de ingresos extra por categoría (experiencia, foto, filiales, etc.).
     * Toma todos los eventos de todas las temporadas y los clasifica.
     *
     * @type {import('vue').ComputedRef<Array<{key: string, revenue: number, events: number, attendance: number}>>}
     */
    const extraIncomeBreakdown = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const acc = {};
        for (const [, entry] of Object.entries(perSeason)) {
            const activity = entry?.activity;
            if (!activity?.length) continue;
            for (const ev of activity) {
                const cat = classifyEvent(ev.event_name || '');
                if (!acc[cat]) acc[cat] = { revenue: 0, events: 0, attendance: 0 };
                acc[cat].revenue += ev.total_revenue || 0;
                acc[cat].events += 1;
                acc[cat].attendance += ev.total_attendance || 0;
            }
        }
        // Orden: respetar orden de CATEGORY_RULES + otros al final
        const order = CATEGORY_RULES.map(r => r.key);
        const sorted = Object.entries(acc).sort(([a], [b]) => {
            const ia = order.indexOf(a);
            const ib = order.indexOf(b);
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
        return sorted.map(([key, data]) => ({ key, ...data }));
    });

    return {
        data: base.data,
        loading: base.loading,
        error: base.error,
        isMultiSeason: base.isMultiSeason,
        summaryKpis,
        extraIncomeBreakdown,
        eventsTable,
        activityComparison,
        activityDelta,
        seasonBreakdown,
        refresh: base.refresh,
    };
}
